import { useAdminStore } from '@/store/admin';
import { useAuthStore } from '@/store/auth';
import axios from 'axios';

export const publicAxios = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_API_BASE_URL}/v1/api/`,
  headers: {
    'Content-Type': 'application/json',
  },
});
export const publicContentAxios = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_CONTENT_API_BASE_URL}/v1/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const protectedAxios = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_API_BASE_URL}/v1/api/`,
  headers: {
    'Content-Type': 'application/json',
  },
});
export const protectedContentAxios = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_CONTENT_API_BASE_URL}/v1/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

protectedAxios.interceptors.request.use(
  (config) => {
    const { token } = useAuthStore.getState();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) =>
    error ? prom.reject(error) : prom.resolve(token!)
  );
  failedQueue = [];
};

// Interceptor to catch 401 errors and attempt token refresh
protectedAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark request as retried to avoid infinite loops
      originalRequest._retry = true;

      if (isRefreshing) {
        // If refresh is in progress, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            return protectedAxios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      const { refreshToken } = useAuthStore.getState();
      try {
        const {
          data: { data },
        } = await publicAxios.post('auth/refresh-token', {
          refreshToken,
        });
        useAuthStore.getState().setToken(data.accessToken);
        if (data.refreshToken) {
          useAuthStore.getState().setRefreshToken(data.refreshToken);
        }
        // Update default header for future requests
        protectedAxios.defaults.headers.common['Authorization'] =
          'Bearer ' + data.token;
        processQueue(null, data.token);
        return protectedAxios(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // Clear auth state if refresh fails
        useAuthStore.getState().clearAuth();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

protectedContentAxios.interceptors.request.use(
  (config) => {
    const { token } = useAuthStore.getState();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

protectedContentAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark request as retried to avoid infinite loops
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            return protectedContentAxios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      const { refreshToken } = useAuthStore.getState();
      if (!refreshToken) {
        useAuthStore.getState().clearAuth();
        // redirect to login page
        window.location.href = '/sign-in';
        return;
      }
      try {
        const {
          data: { data },
        } = await publicAxios.post('auth/refresh-token', {
          refreshToken,
        });
        useAuthStore.getState().setToken(data.accessToken);
        if (data.refreshToken) {
          useAuthStore.getState().setRefreshToken(data.refreshToken);
        }
        // Update default header for future requests
        protectedContentAxios.defaults.headers.common['Authorization'] =
          'Bearer ' + data.token;
        processQueue(null, data.token);
        return protectedContentAxios(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // Clear auth state if refresh fails
        useAuthStore.getState().clearAuth();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// notifications axios instance
export const notificationsPublicAxios = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_NOTIFICATIONS_API_BASE_URL}/v1/api/notifications`,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const notificationsProtectedAxios = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_NOTIFICATIONS_API_BASE_URL}/v1/api/notifications`,
  headers: {
    'Content-Type': 'application/json',
  },
});

notificationsProtectedAxios.interceptors.request.use(
  (config) => {
    const { token } = useAuthStore.getState();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

notificationsProtectedAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark request as retried to avoid infinite loops
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            return notificationsProtectedAxios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      const { refreshToken } = useAuthStore.getState();
      try {
        const {
          data: { data },
        } = await publicAxios.post('auth/refresh-token', {
          refreshToken,
        });
        useAuthStore.getState().setToken(data.accessToken);
        if (data.refreshToken) {
          useAuthStore.getState().setRefreshToken(data.refreshToken);
        }
        // Update default header for future requests
        protectedContentAxios.defaults.headers.common['Authorization'] =
          'Bearer ' + data.token;
        processQueue(null, data.token);
        return protectedContentAxios(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // Clear auth state if refresh fails
        useAuthStore.getState().clearAuth();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// admin axios instance
export const adminAxios = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_API_BASE_URL}/v1/api/`,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminAxios.interceptors.request.use(
  (config) => {
    const { token } = useAdminStore.getState();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

adminAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark request as retried to avoid infinite loops
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            return adminAxios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      const { refreshToken } = useAdminStore.getState();
      try {
        const {
          data: { data },
        } = await publicAxios.post('auth/refresh-token', {
          refreshToken,
        });
        useAdminStore.getState().setToken(data.accessToken);
        if (data.refreshToken) {
          useAdminStore.getState().setRefreshToken(data.refreshToken);
        }
        // Update default header for future requests
        protectedContentAxios.defaults.headers.common['Authorization'] =
          'Bearer ' + data.token;
        processQueue(null, data.token);
        return protectedContentAxios(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // Clear auth state if refresh fails
        useAdminStore.getState().clearAuth();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);
